import React, { useState } from 'react';

// Library Imports
import { makeStyles } from '@material-ui/core/styles';
import AnchorLink from 'react-anchor-link-smooth-scroll';

// React Router
import { useHistory } from 'react-router';

// MUI Components
import AppBar from '@material-ui/core/AppBar';
import Toolbar from '@material-ui/core/Toolbar';
import clsx from 'clsx';
import Drawer from '@material-ui/core/Drawer';
import IconButton from '@material-ui/core/IconButton';
import MenuItem from '@material-ui/core/MenuItem';
import Menu from '@material-ui/core/Menu';
import Logo from '../static/images/TJSymbol.svg';
import List from '@material-ui/core/List';
import Divider from '@material-ui/core/Divider';
import MenuIcon from '@material-ui/icons/Menu';
import CloseOutlinedIcon from '@material-ui/icons/CloseOutlined';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import Button from '@material-ui/core/Button';

// Icons Imports
import MoreIcon from '@material-ui/icons/MoreVert';
import apis from '../helpers/apis';

const useStyles = makeStyles((theme) => ({
  menuButton: {
    marginRight: theme.spacing(2),
  },
  toolbar: {
    display: 'flex',
    backgroundColor: '#ffffff',
    justifyContent: 'space-between',
  },
  appBar: {
    boxShadow: 'none',
    transition: theme.transitions.create(['margin', 'width'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },
  appBarShift: {
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(['margin', 'width'], {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginRight: drawerWidth,
  },
  hide: {
    display: 'none',
  },
  drawer: {
    width: drawerWidth,
    flexShrink: 0,
    position: 'relative',
  },
  drawerPaper: {
    width: drawerWidth,
    boxShadow: '-5px 0px 6px #00000029',
    borderLeft: 'none',
  },
  drawerTitle: {
    margin: 12,
    textTransform: 'uppercase',
    textAlign: 'center',
  },
  drawerHeader: {
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'row',
    padding: theme.spacing(0, 1),
    background: '#034EA2',

    // necessary for content to be below app bar
    ...theme.mixins.toolbar,
    justifyContent: 'space-around',
    color: '#fff',
    fontSize: 24,
    fontFamily: 'Montserrat-Regular',
  },
  drawerBody: {
    padding: '20px 40px',
  },
  listItem: {
    borderBottom: '1px solid #707070',

    '&:hover': {
      color: '#00C1FF',
    },

    '&.active': {
      color: '#00C1FF',
    },
  },
  NavlistItem: {
    '&:hover': {
      color: '#00C1FF',
    },
    // commented out until responsive refactor
    // '&.active': {
    //   color: '#EE236B',
    // },
  },
  content: {
    flexGrow: 1,
    padding: theme.spacing(3),
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    marginRight: -drawerWidth,
  },
  contentShift: {
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginRight: 0,
  },
  title: {
    display: 'none',
    [theme.breakpoints.up('sm')]: {
      display: 'block',
    },
  },
  userActionsIcon: {
    color: '#333',
  },
  userActionsIconCaret: {
    fontSize: 14,
    marginLeft: 10,
    color: '#333',
  },
  navLogo: {
    width: 30,
    '&:hover': {
      cursor: 'pointer',
    },
  },
  search: {
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'row',
    flexGrow: 1,
    justifyContent: 'center',
    position: 'relative',
    [theme.breakpoints.up('sm')]: {
      marginLeft: theme.spacing(3),
      width: 'auto',
    },
  },
  searchPreText: {
    color: '#000',
    marginRight: 10,
  },
  searchDropdown: {
    width: '30%',
    [theme.breakpoints.down('md')]: {
      width: '100%',
    },
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
  },
  inputRoot: {
    color: '#000',
    width: '100%',
    [theme.breakpoints.down('sm')]: {
      width: '80%',
    },
  },
  userIcon: {
    color: '#333',
    '&:hover': {
      background: 'none',
    },
  },
  sectionDesktop: {
    display: 'none',
    [theme.breakpoints.up('md')]: {
      width: '100%',
      fontSize: 14,
      display: 'flex',
      flexDirection: 'row',
      color: '#333',
      alignItems: 'center',
      // justifyContent: "space-evenly",
      marginLeft: 50,
    },
  },
  desktopItemText: {
    fontSize: 12,
    color: '#64646e',
    marginRight: 5,
  },
  desktopItemLink: {
    fontSize: 14,
    color: '#333',
    marginLeft: -10,
    textUnderline: 'none',
    '&:hover': {
      color: '#00C1FF',
    },
    '&.active': {
      color: '#EE236B',
    },
  },
  pageTitle: {
    flexGrow: 1,
    justifyContent: 'right',
    color: '#C8C8D2',
    fontFamily: 'Montserrat-Regular',
    fontWeight: 800,
  },
  sectionMobile: {
    display: 'flex',
    [theme.breakpoints.up('md')]: {
      display: 'none',
    },
  },
  bottomPush: {
    position: 'fixed',
    bottom: 0,
    display: 'flex',
    width: '100%',
    color: '#fff',
    padding: '10px 40px',
    maxWidth: 360,
    background: '#034EA2',
  },
  bottomPushLink: {
    '&:hover': {
      cursor: 'pointer',
    },
  },
}));

const drawerWidth = 360;

const ApiNavigation = ({ pageTitle, onLogout, mobileMenu, keycloak }) => {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState(null);
  const [anchorAPI, setAnchorAPI] = useState(null);
  const [mobileMoreAnchorEl, setMobileMoreAnchorEl] = useState(null);
  const history = useHistory();
  const isMenuOpen = Boolean(anchorEl);
  const isMobileMenuOpen = Boolean(mobileMoreAnchorEl);
  const [open, setOpen] = React.useState(false);
  const [activeLink, setActiveLink] = useState('overview');
  const currentApi = apis.filter((api) => api.name === pageTitle);
  const apiList = apis.filter(
    (api) =>
      api.name !== 'Internet Payment Gateway' && api.name !== 'POS Integration'
  );

  const handleClickAPI = (event) => {
    setAnchorAPI(event.currentTarget);
  };

  const handleCloseAPI = () => {
    setAnchorAPI(null);
  };

  const handleLogout = () => {
    keycloak.logout({
      redirectUri: `${window.location.protocol}//${window.location.host}`,
    });
  };

  function handleMobileMenuClose() {
    setMobileMoreAnchorEl(null);
  }

  function handleMenuClose() {
    setAnchorEl(null);
    handleMobileMenuClose();
  }

  function handleMobileMenuOpen(event) {
    setMobileMoreAnchorEl(event.currentTarget);
  }

  const menuId = 'primary-search-account-menu';
  const renderMenu = (
    <Menu
      anchorEl={anchorEl}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      id={menuId}
      keepMounted
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      open={isMenuOpen}
      onClose={handleMenuClose}
    >
      <MenuItem onClick={() => onLogout()}>Logout</MenuItem>
    </Menu>
  );

  const mobileMenuId = 'primary-search-account-menu-mobile';
  const renderMobileMenu = (
    <Menu
      anchorEl={mobileMoreAnchorEl}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      id={mobileMenuId}
      keepMounted
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      open={isMobileMenuOpen}
      onClose={handleMobileMenuClose}
    >
      {/* TODO The below may change - will discuss */}
      {/* <MenuItem onClick={handleProfileMenuOpen}>
        <IconButton
          aria-label="account of current user"
          aria-controls="primary-search-account-menu"
          aria-haspopup="true"
          color="inherit"
        >
          <FontAwesomeIcon icon={faUser} />
        </IconButton>
        <p>Change Password</p>
      </MenuItem> */}
    </Menu>
  );

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  return (
    <>
      <AppBar
        position="fixed"
        className={clsx(classes.appBar, {
          [classes.appBarShift]: open,
        })}
      >
        <Toolbar className={classes.toolbar}>
          <img
            src={Logo}
            className={classes.navLogo}
            width="250"
            onClick={() => history.push('/')}
            alt="Transaction Junction Logo"
          />
          <div className={classes.sectionDesktop}>
            {/* possible home button */}

            {/* <AnchorLink>
              <MenuItem
                onClick={() => history.push("/")}
                className={[
                  classes.NavlistItem,
                  activeLink === "/" ? `active` : "",
                ]}
                button
              >
                <ListItemText primary="Home" />
              </MenuItem>
            </AnchorLink> */}
            {currentApi[0].navItems.map((item) => (
              <AnchorLink href={`#${item.slug}`}>
                <MenuItem
                  onClick={() => setActiveLink(item.slug)}
                  className={[
                    classes.NavlistItem,
                    activeLink === item.slug ? `active` : '',
                  ]}
                  button
                  key={item.slug}
                >
                  <ListItemText primary={item.name} />
                </MenuItem>
              </AnchorLink>
            ))}
            <Button
              aria-controls="api-menu"
              aria-haspopup="true"
              onClick={handleClickAPI}
              style={{ textTransform: 'none' }}
            >
              APIs
            </Button>
            <Menu
              id="api-menu"
              anchorEl={anchorAPI}
              keepMounted
              open={Boolean(anchorAPI)}
              onClose={handleCloseAPI}
              className={classes.desktopItemLink}
              style={{
                marginTop: 40,
              }}
            >
              <MenuItem
                onClick={handleCloseAPI}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                }}
              >
                {apiList.map((item) => (
                  <MenuItem
                    onClick={() =>
                      history.push({
                        pathname: `/${item.slug}`,
                      })
                    }
                    className={[
                      classes.NavlistItem,
                      activeLink === item.slug ? `active` : '',
                    ]}
                    button
                    key={item.slug}
                  >
                    <ListItemText primary={item.name} />
                  </MenuItem>
                ))}
              </MenuItem>
            </Menu>
          </div>
          <IconButton
            color="#000"
            aria-label="open drawer"
            edge="end"
            onClick={handleDrawerOpen}
            className={clsx(open && classes.hide)}
          >
            <MenuIcon />
          </IconButton>
          {mobileMenu ? (
            <div className={classes.sectionMobile}>
              <IconButton
                aria-label="show more"
                aria-controls={mobileMenuId}
                aria-haspopup="true"
                onClick={handleMobileMenuOpen}
                color="#333"
              >
                <MoreIcon />
              </IconButton>
            </div>
          ) : (
            ''
          )}
        </Toolbar>
      </AppBar>

      {renderMobileMenu}
      {renderMenu}

      {/* <TJHamburgerMenu /> */}
      <Drawer
        className={classes.drawer}
        variant="persistent"
        anchor="right"
        open={open}
        classes={{
          paper: classes.drawerPaper,
        }}
      >
        <div className={classes.drawerHeader}>
          <span className={classes.drawerTitle}>{pageTitle}</span>
          <IconButton onClick={handleDrawerClose}>
            <CloseOutlinedIcon style={{ color: '#fff' }} />
          </IconButton>
        </div>
        <Divider />
        <div className={classes.drawerBody}>
          <List>
            {currentApi[0].navItems.map((item) => (
              <AnchorLink href={`#${item.slug}`}>
                <ListItem
                  onClick={() => setActiveLink(item.slug)}
                  className={[
                    classes.listItem,
                    activeLink === item.slug ? `active` : '',
                  ]}
                  button
                  key={item.slug}
                >
                  <ListItemText primary={item.name} />
                </ListItem>
              </AnchorLink>
            ))}
            <AnchorLink href="#page-api">
              <MenuItem>APIs</MenuItem>
            </AnchorLink>
          </List>
        </div>
        <div className={classes.bottomPush}>
          <div>
            user |{' '}
            <span onClick={handleLogout} className={classes.bottomPushLink}>
              LOGOUT
            </span>
          </div>
        </div>
      </Drawer>
    </>
  );
};

export default ApiNavigation;
